import { Trash2, RotateCcw } from 'lucide-react';
import type { CampaignWithComputed } from '../types/campaign';
import type { UserRole } from '../types/user';
import { canEditCampaign, canDeleteCampaign } from '../lib/permissions';
import { statusTransitionService } from '../services/statusTransitionService';
import { getStatusBadgeClass } from '../utils/statusColors';
import { parseJsonArray } from '../lib/jsonColumn';
import { formatEuro } from '../utils/currency';
import { deriveInfluencerInvoiceStatus } from '../utils/paymentCalculations';
import { isAvenca } from '../utils/avenca';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useColumnResize } from '../hooks/useColumnResize';
import type { ColumnWidths } from '../hooks/useUserPreferences';

type CampaignModeFilter = 'all' | 'brand_action' | 'influencer_marketing' | 'sponsorship' | 'services';

interface TableViewProps {
  campaigns: CampaignWithComputed[];
  onEdit: (campaign: CampaignWithComputed) => void;
  onDelete: (id: string) => void;
  userRole: UserRole | null;
  campaignModeFilter?: CampaignModeFilter;
  currentUserId?: string;
}

const DEFAULT_COLUMN_WIDTHS: ColumnWidths = {
  agent: 150,
  workCode: 120,
  brand: 180,
  campaignRef: 150,
  influencer: 200,
  campaignStatus: 180,
  fee: 120,
  netFee: 120,
  commission: 120,
  deliverables: 180,
  mohitInvoice: 220,
  influencerInvoice: 220,
  daysToDue: 120,
  actions: 100,
  eventDate: 120,
  sponsor: 180,
  sponsorshipAmount: 150,
};

interface ResizeHandleProps {
  columnKey: string;
  onResizeStart: (columnKey: string, startX: number) => void;
  isResizing: boolean;
}

function ResizeHandle({ columnKey, onResizeStart, isResizing }: ResizeHandleProps) {
  return (
    <div
      className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-brand-lime ${
        isResizing ? 'bg-brand-ink' : 'bg-transparent'
      } transition-colors group-hover:bg-brand-lime/60`}
      onMouseDown={(e) => {
        e.preventDefault();
        onResizeStart(columnKey, e.clientX);
      }}
      style={{ userSelect: 'none' }}
    />
  );
}

export function TableView({ campaigns, onEdit, onDelete, userRole, campaignModeFilter = 'all', currentUserId }: TableViewProps) {
  const { t, ts } = useLanguage();
  const { showConfirm } = useModal();
  const { preferences, updateColumnWidths } = useUserPreferences();

  const isSponsorship = campaignModeFilter === 'sponsorship';
  const isStandard = campaignModeFilter === 'brand_action' || campaignModeFilter === 'influencer_marketing';
  const isAll = campaignModeFilter === 'all';

  const {
    columnWidths,
    isResizing,
    resizingColumn,
    handleResizeStart,
    getColumnStyle,
    resetWidths,
  } = useColumnResize({
    defaultWidths: DEFAULT_COLUMN_WIDTHS,
    savedWidths: preferences?.table_column_widths,
    onSave: updateColumnWidths,
    minWidth: 80,
    maxWidth: 600,
  });

  // A campaign with no costs (services without payables) has nothing to pay
  // out: its creator side is settled by definition
  const hasNoPayables = (c: CampaignWithComputed) => (c as { has_payables?: boolean | null }).has_payables === false;
  const isFullyPaid = (c: CampaignWithComputed) =>
    c.campaign_status === 'Completed'
    && c.company_invoice_status === 'Paid'
    && (hasNoPayables(c) || deriveInfluencerInvoiceStatus(c) === 'Paid');

  // Sort campaigns: completed & fully paid go to bottom
  const sortedCampaigns = [...campaigns].sort((a, b) => {
    const aFullyPaid = isFullyPaid(a);
    const bFullyPaid = isFullyPaid(b);

    if (aFullyPaid && !bFullyPaid) return 1;
    if (!aFullyPaid && bFullyPaid) return -1;
    return 0;
  });

  const ongoingCampaigns = sortedCampaigns.filter(c => !isFullyPaid(c));

  const completedCampaigns = sortedCampaigns.filter(c => isFullyPaid(c));


  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCampaignStatus = (campaign: CampaignWithComputed) => {
    return campaign.campaign_status;
  };

  const getRowColor = (color: string | null) => {
    switch (color) {
      case 'green':
        return 'bg-emerald-50/40 hover:bg-emerald-50/70';
      case 'red':
        return 'bg-rose-50/40 hover:bg-rose-50/70';
      case 'orange':
        return 'bg-orange-50/40 hover:bg-orange-50/70';
      case 'yellow':
        return 'bg-amber-50/40 hover:bg-amber-50/70';
      case 'grey':
        return 'bg-slate-50/60 hover:bg-slate-100/60';
      default:
        return 'bg-white hover:bg-gray-50/80';
    }
  };

  const getStatusBadge = (status: string, highlight?: boolean) => {
    const colorClass = highlight ? 'bg-amber-50 text-amber-700' : getStatusBadgeClass(status, 'soft');

    return (
      <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full leading-tight-ui ${colorClass}`}>
        {ts(status)}
      </span>
    );
  };

  const getLastDeliverableStatus = (campaign: CampaignWithComputed): string | null => {
    const deliverables = parseJsonArray<{ status?: string }>(campaign.deliverables);
    if (deliverables.length > 0) {
      return deliverables[deliverables.length - 1].status || 'Draft';
    }
    // Sponsorships and services have no deliverables — nothing to stage
    if (campaign.campaign_mode === 'sponsorship' || campaign.campaign_mode === 'services') return null;
    return campaign.delivery_status || 'Draft';
  };

  const showInfluencerColumn = !isSponsorship;
  const showDeliverablesColumn = !isSponsorship;
  const showFeeColumn = !isSponsorship;
  const showCommissionColumns = !isSponsorship && userRole?.can_view_commissions;
  const showEventDateColumn = isSponsorship;
  const showSponsorColumn = isSponsorship;
  const showSponsorshipAmountColumn = isSponsorship;

  const renderCampaignRow = (campaign: CampaignWithComputed) => {
    const campaignStatus = getCampaignStatus(campaign);
    const deliverablesStatus = getLastDeliverableStatus(campaign);
    const shouldHighlightMohitInvoice = campaign.company_invoice_status === 'Not Issued' && deliverablesStatus === 'Published';
    const shouldHighlightInfluencerInvoice = campaign.company_invoice_status === 'Paid';
    const stageRowClass = statusTransitionService.getStageRowClass(campaignStatus as any);
    const isSponsorshipCampaign = campaign.campaign_mode === 'sponsorship';
    const sponsorshipAmount = (campaign as any).sponsorship_amount || 0;
    const eventDate = (campaign as any).event_date;

    const canEdit = canEditCampaign(userRole, campaign, currentUserId);

    return (
      <tr
        key={campaign.id}
        className={`${getRowColor(campaign.row_color)} ${stageRowClass} transition-colors ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
        onClick={() => canEdit && onEdit(campaign)}
      >
        <td className="px-5 py-5 text-sm text-gray-900 font-medium overflow-hidden text-ellipsis leading-compact" style={getColumnStyle('agent')}>{campaign.agent_name || '-'}</td>
        <td className="px-5 py-5 text-sm font-semibold overflow-hidden text-emerald-600 leading-compact" style={getColumnStyle('workCode')}>
          {campaign.work_code}
        </td>
        <td className="px-5 py-5 text-sm text-gray-900 font-medium overflow-hidden text-ellipsis leading-compact" style={getColumnStyle('brand')}>{campaign.brand || '-'}</td>
        {showEventDateColumn && (
          <td className="px-5 py-5 text-sm text-gray-600 overflow-hidden text-ellipsis leading-compact" style={getColumnStyle('eventDate')}>
            {isSponsorshipCampaign ? formatDate(eventDate) : '-'}
          </td>
        )}
        {showSponsorColumn && (
          <td className="px-5 py-5 text-sm text-gray-900 font-medium overflow-hidden text-ellipsis leading-compact" style={getColumnStyle('sponsor')}>{campaign.client || '-'}</td>
        )}
        {!showSponsorColumn && (
          <td className="px-5 py-5 text-sm text-gray-600 overflow-hidden text-ellipsis leading-compact" style={getColumnStyle('campaignRef')}>{campaign.campaign_ref || '-'}</td>
        )}
        {showInfluencerColumn && (
          <td className="px-5 py-5 text-sm text-gray-900 font-medium overflow-hidden text-ellipsis leading-compact" style={getColumnStyle('influencer')}>{campaign.influencer || '-'}</td>
        )}
        <td className="px-5 py-5 text-sm overflow-hidden" style={getColumnStyle('campaignStatus')}>
          <div className="flex items-center gap-2">
            {getStatusBadge(campaignStatus)}
            {campaign.show_billing_tag && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                {ts('Billing')}
              </span>
            )}
          </div>
        </td>
        {showSponsorshipAmountColumn && (
          <td className="px-5 py-5 text-sm text-gray-900 font-semibold overflow-hidden text-ellipsis leading-compact tabular-nums" style={getColumnStyle('sponsorshipAmount')}>
            {isSponsorshipCampaign ? formatEuro(sponsorshipAmount) : '-'}
          </td>
        )}
        {showFeeColumn && (
          <td className="px-5 py-5 text-sm text-gray-900 font-semibold overflow-hidden text-ellipsis leading-compact tabular-nums" style={getColumnStyle('fee')}>
            {formatEuro(campaign.fee)}
            {isAvenca(campaign as any) && (
              <span className="block text-[11px] font-medium text-gray-500">
                {t('avenca.tableCaption').replace('{0}', formatEuro(Number((campaign as any).monthly_amount) || 0))}
              </span>
            )}
          </td>
        )}
        {showCommissionColumns && (
          <>
            <td className="px-5 py-5 text-sm text-gray-900 font-semibold overflow-hidden text-ellipsis leading-compact tabular-nums" style={getColumnStyle('netFee')}>{formatEuro(campaign.net_fee_influencer)}</td>
            <td className="px-5 py-5 text-sm text-gray-600 overflow-hidden text-ellipsis leading-compact tabular-nums" style={getColumnStyle('commission')}>{formatEuro(campaign.commission_amount)}</td>
          </>
        )}
        {showDeliverablesColumn && (
          <td className="px-5 py-5 text-sm overflow-hidden" style={getColumnStyle('deliverables')}>{deliverablesStatus ? getStatusBadge(deliverablesStatus) : <span className="text-gray-300">—</span>}</td>
        )}
        <td className="px-5 py-5 text-sm overflow-hidden" style={getColumnStyle('mohitInvoice')}>
          <div className="flex items-center gap-2">
            {getStatusBadge(campaign.company_invoice_status, shouldHighlightMohitInvoice)}
            {campaign.company_invoice_number && campaign.company_invoice_status !== 'Not Issued' && (
              <span className="text-xs text-gray-400 leading-small">#{campaign.company_invoice_number}</span>
            )}
          </div>
        </td>
        <td className="px-5 py-5 text-sm overflow-hidden" style={getColumnStyle('influencerInvoice')}>{hasNoPayables(campaign)
          ? <span className="text-xs font-medium text-gray-400" title={t('table.noPayablesHint')}>{t('table.notApplicable')}</span>
          : getStatusBadge(campaign.influencer_invoice_status, shouldHighlightInfluencerInvoice)}</td>
        <td className="px-5 py-5 text-sm text-gray-600 overflow-hidden text-ellipsis leading-compact tabular-nums" style={getColumnStyle('daysToDue')}>
          {campaign.days_remaining_to_due !== null ? (
            <span className={campaign.days_remaining_to_due < 0 ? 'text-rose-600 font-semibold' : ''}>
              {campaign.days_remaining_to_due > 0 ? `${campaign.days_remaining_to_due}d` : `${Math.abs(campaign.days_remaining_to_due)}d overdue`}
            </span>
          ) : '-'}
        </td>
        <td className="px-5 py-5 text-sm text-right overflow-hidden" style={getColumnStyle('actions')} onClick={(e) => e.stopPropagation()}>
          {canDeleteCampaign(userRole, campaign, currentUserId) && (
            <button
              onClick={async () => {
                const confirmed = await showConfirm({
                  title: t('table.deleteCampaign'),
                  message: t('table.deleteConfirm'),
                  confirmText: t('table.deleteCampaign'),
                  cancelText: t('common.cancel'),
                  variant: 'danger',
                });
                if (confirmed) {
                  onDelete(campaign.id);
                }
              }}
              className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-full transition-colors"
              title="Delete campaign"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </td>
      </tr>
    );
  };

  if (campaigns.length === 0) {
    return (
      <div className="bg-white rounded-card shadow-card p-12 text-center">
        <p className="text-gray-500 text-lg leading-reading">{t('table.noData')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-card shadow-card overflow-hidden">
      <div className="flex items-center justify-between px-6 py-3 bg-white">
        <span className="text-[11px] text-gray-400 leading-small tracking-wide">{t('table.dragToResize')}</span>
        <button
          onClick={resetWidths}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-full transition-colors leading-compact"
          title={t('table.resetWidths')}
        >
          <RotateCcw className="w-3 h-3" />
          {t('table.resetWidths')}
        </button>
      </div>
      <div className="overflow-x-auto table-scroll-wrapper scrollbar-thin" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
        <table className="w-full" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest leading-compact" style={getColumnStyle('agent')}>
                {t('table.agent')}
                <ResizeHandle columnKey="agent" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'agent'} />
              </th>
              <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('workCode')}>
                {t('table.workCode')}
                <ResizeHandle columnKey="workCode" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'workCode'} />
              </th>
              <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('brand')}>
                {isSponsorship ? t('sponsorship.eventName') : t('table.brand')}
                <ResizeHandle columnKey="brand" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'brand'} />
              </th>
              {showEventDateColumn && (
                <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('eventDate')}>
                  {t('sponsorship.eventDate')}
                  <ResizeHandle columnKey="eventDate" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'eventDate'} />
                </th>
              )}
              {showSponsorColumn && (
                <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('sponsor')}>
                  {t('sponsorship.sponsor')}
                  <ResizeHandle columnKey="sponsor" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'sponsor'} />
                </th>
              )}
              {!showSponsorColumn && (
                <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('campaignRef')}>
                  {t('table.campaignRef')}
                  <ResizeHandle columnKey="campaignRef" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'campaignRef'} />
                </th>
              )}
              {showInfluencerColumn && (
                <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('influencer')}>
                  {t('table.influencer')}
                  <ResizeHandle columnKey="influencer" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'influencer'} />
                </th>
              )}
              <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('campaignStatus')}>
                {t('table.campaignStatus')}
                <ResizeHandle columnKey="campaignStatus" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'campaignStatus'} />
              </th>
              {showSponsorshipAmountColumn && (
                <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('sponsorshipAmount')}>
                  {t('sponsorship.sponsorshipFee')}
                  <ResizeHandle columnKey="sponsorshipAmount" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'sponsorshipAmount'} />
                </th>
              )}
              {showFeeColumn && (
                <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('fee')}>
                  {t('table.fee')}
                  <ResizeHandle columnKey="fee" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'fee'} />
                </th>
              )}
              {showCommissionColumns && (
                <>
                  <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('netFee')}>
                    {t('table.netInfluencer')}
                    <ResizeHandle columnKey="netFee" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'netFee'} />
                  </th>
                  <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('commission')}>
                    {t('table.commission')}
                    <ResizeHandle columnKey="commission" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'commission'} />
                  </th>
                </>
              )}
              {showDeliverablesColumn && (
                <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('deliverables')}>
                  {t('table.deliverablesStatus')}
                  <ResizeHandle columnKey="deliverables" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'deliverables'} />
                </th>
              )}
              <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('mohitInvoice')}>
                {isSponsorship ? t('sponsorship.tab.receivables') : t('table.mohitInvoice')}
                <ResizeHandle columnKey="mohitInvoice" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'mohitInvoice'} />
              </th>
              <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('influencerInvoice')}>
                {isSponsorship ? t('sponsorship.tab.payables') : t('table.influencerInvoice')}
                <ResizeHandle columnKey="influencerInvoice" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'influencerInvoice'} />
              </th>
              <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('daysToDue')}>
                {t('table.daysToDue')}
                <ResizeHandle columnKey="daysToDue" onResizeStart={handleResizeStart} isResizing={resizingColumn === 'daysToDue'} />
              </th>
              <th className="sticky top-0 bg-brand-tint border-b border-brand-line relative group px-5 py-3.5 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={getColumnStyle('actions')}>
                {t('table.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {ongoingCampaigns.map(renderCampaignRow)}
            {completedCampaigns.length > 0 && (
              <tr className="bg-gray-50/60" style={{ position: 'relative' }}>
                <td colSpan={99} className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                  {t('table.completedCampaigns')}
                </td>
              </tr>
            )}
            {completedCampaigns.map(renderCampaignRow)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
